/**
 * Grafana Faro 初期化モジュール
 *
 * ブラウザアプリケーションのエントリーポイントで最初に呼び出す。
 * Grafana Cloud へエラー、パフォーマンス、トレースを送信する。
 * @graph-stack product-innovation-lab
 * @graph-domain Infrastructure
 * @graph-business Grafana Faro Web SDKの初期化設定。フロントエンドエラー・パフォーマンス監視
 * @graph-connects none
 */
import { initializeFaro as initFaro, getWebInstrumentations, } from '@grafana/faro-web-sdk';
import { TracingInstrumentation } from '@grafana/faro-web-tracing';
// ============================================================
// Module State
// ============================================================
/** @graph-connects none */
let faroInstance = null;
// ============================================================
// Public Functions
// ============================================================
/**
 * Grafana Faro SDKを初期化する
 *
 * @example
 * ```typescript
 * // apps/web/product-innovation-lab/src/main.tsx の最初に
 * import { initFaro } from '@cortex/faro';
 * initFaro({ appName: 'product-innovation-lab' });
 *
 * // 以降は通常のReactコード...
 * ```
 * @graph-connects none
 */
export function initializeFaro(config) {
    if (faroInstance) {
        // oxlint-disable-next-line cortex-logging/prefer-structured-logging -- Faro SDK itself is the logging infrastructure
        console.warn('[Faro] SDK already initialized, skipping');
        return faroInstance;
    }
    const { appName, appVersion = 'unknown', environment = import.meta.env.MODE, collectorUrl = import.meta.env.VITE_FARO_COLLECTOR_URL, enableTracing = true, captureConsole = false, } = config;
    // コレクターURLが設定されていない場合はスキップ（ローカル開発時）
    if (!collectorUrl) {
        // oxlint-disable-next-line cortex-logging/prefer-structured-logging -- Faro SDK itself is the logging infrastructure
        console.info('[Faro] VITE_FARO_COLLECTOR_URL not set, skipping initialization');
        return null;
    }
    // 計装設定
    const instrumentations = [
        ...getWebInstrumentations({
            captureConsole,
        }),
    ];
    // トレーシング計装を追加
    if (enableTracing) {
        instrumentations.push(new TracingInstrumentation());
    }
    // Faro初期化
    const faro = initFaro({
        url: collectorUrl,
        app: {
            name: appName,
            version: appVersion,
            environment,
        },
        instrumentations,
    });
    // oxlint-disable-next-line cortex-logging/prefer-structured-logging -- Faro SDK itself is the logging infrastructure
    console.info(`[Faro] Initialized for ${appName} (${environment})`);
    faroInstance = faro;
    return faro;
}
/**
 * Faroインスタンスを取得する
 * @graph-connects none
 */
export function getFaro() {
    return faroInstance;
}
/**
 * Faroが初期化済みかどうか
 * @graph-connects none
 */
export function isFaroInitialized() {
    return faroInstance !== null;
}
/**
 * カスタムイベントを記録する
 * @graph-connects none
 */
export function logEvent(name, attributes) {
    if (faroInstance) {
        faroInstance.api.pushEvent(name, attributes);
    }
}
/**
 * カスタムエラーを記録する
 * @graph-connects none
 */
export function logError(error, context) {
    if (faroInstance) {
        faroInstance.api.pushError(error, { context });
    }
}
/**
 * ユーザー情報を設定する
 * @graph-connects none
 */
export function setUser(userId, email, attributes) {
    if (faroInstance) {
        faroInstance.api.setUser({
            id: userId,
            email,
            attributes,
        });
    }
}
/**
 * ユーザー情報をクリアする
 * @graph-connects none
 */
export function clearUser() {
    if (faroInstance) {
        faroInstance.api.resetUser();
    }
}
//# sourceMappingURL=index.js.map